import type {
  AgentRecord,
  AgentToolRecord,
  IntentProfileRecord,
  ToolRecord,
} from '../lib/catalog';

type Point={x:number;y:number};

function polar(index:number,total:number,radius:number,start=-Math.PI/2):Point{
  if(total<=1)return {x:50,y:50-radius};
  const angle=start+(Math.PI*2*index)/total;
  return {x:50+Math.cos(angle)*radius,y:50+Math.sin(angle)*radius};
}

type Props={
  platformName:string;
  nodeName:string;
  profile:IntentProfileRecord|null;
  agents:AgentRecord[];
  tools:ToolRecord[];
  agentTools:AgentToolRecord[];
};

export default function AgentConstellation({
  platformName,
  nodeName,
  profile,
  agents,
  tools,
  agentTools,
}:Props){
  if(!profile){
    return (
      <section className="constellation-shell constellation-idle" aria-label="Agent constellation">
        <div className="constellation-core">
          <span>{platformName}</span>
          <em>{nodeName}</em>
          <strong>AWAITING INTENT</strong>
        </div>
        <div className="idle-orbit orbit-a"/>
        <div className="idle-orbit orbit-b"/>
      </section>
    );
  }

  const agentPoints=new Map<string,Point>();
  const toolPoints=new Map<string,Point>();

  agents.forEach((agent,index)=>{
    agentPoints.set(agent.id,polar(index,agents.length,27,-Math.PI/2));
  });

  tools.forEach((tool,index)=>{
    toolPoints.set(tool.id,polar(index,tools.length,43,-Math.PI/2+.23));
  });

  const relationSet=new Set(
    agentTools.map(link=>`${link.agent_id}:${link.tool_id}`)
  );

  return (
    <section className="constellation-shell" aria-label={`${profile.label} agent constellation`}>
      <svg className="constellation-links" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {agents.map(agent=>{
          const point=agentPoints.get(agent.id)!;
          return (
            <line
              key={`core-${agent.id}`}
              className="constellation-link agent-link"
              x1="50" y1="50" x2={point.x} y2={point.y}
            />
          );
        })}
        {agents.flatMap(agent=>{
          const from=agentPoints.get(agent.id)!;
          return tools
            .filter(tool=>relationSet.has(`${agent.id}:${tool.id}`))
            .map(tool=>{
              const to=toolPoints.get(tool.id)!;
              return (
                <line
                  key={`${agent.id}-${tool.id}`}
                  className="constellation-link tool-link"
                  x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                />
              );
            });
        })}
      </svg>

      <div className="constellation-ring ring-inner"/>
      <div className="constellation-ring ring-outer"/>

      <div className="constellation-core active-core">
        <span>{platformName}</span>
        <em>{nodeName} · {profile.label}</em>
        <strong>ORCHESTRATING</strong>
      </div>

      {agents.map(agent=>{
        const point=agentPoints.get(agent.id)!;
        return (
          <div
            className="constellation-node agent-node"
            key={agent.id}
            style={{left:`${point.x}%`,top:`${point.y}%`}}
            title={agent.role}
          >
            <span>AGENT</span>
            <strong>{agent.name.replace(' Agent','')}</strong>
          </div>
        );
      })}

      {tools.map(tool=>{
        const point=toolPoints.get(tool.id)!;
        return (
          <div
            className="constellation-node tool-node"
            key={tool.id}
            style={{left:`${point.x}%`,top:`${point.y}%`}}
            title={tool.category}
          >
            <span>TOOL</span>
            <strong>{tool.name}</strong>
          </div>
        );
      })}
    </section>
  );
}
